import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { CalendarClock, AlertTriangle, Sparkles } from "lucide-react";

/**
 * Compact subscription timer for the topbar.
 * - Always shows X days left + plan name (subtle pill)
 * - Adds a prominent "Renew now" CTA when days_left ≤ 30
 * - Switches to rose-red "Expired — renew" when days_left ≤ 0
 * - Hidden for super_admin (they have no per-hospital plan)
 */
export default function SubscriptionTimer({ userRole }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!userRole || userRole === "super_admin") return;
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get("/payments/my-plan");
        if (mounted) setData(data);
      } catch (_e) { /* ignore — sidebar shouldn't break for missing plan */ }
    })();
    return () => { mounted = false; };
  }, [userRole]);

  if (!userRole || userRole === "super_admin" || !data) return null;
  const { plan, days_left, status, valid_until } = data;
  if (!plan && status === "no_plan") {
    return (
      <Link
        to="/app/my-plan"
        className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1 text-[11px] font-semibold text-amber-800 hover:bg-amber-200"
        data-testid="topbar-no-plan"
        title="No active subscription"
      >
        <AlertTriangle size={12} />Choose a plan
      </Link>
    );
  }
  if (days_left == null) return null;

  const expired = days_left <= 0 || status === "expired";
  const expiringSoon = !expired && days_left <= 30;

  if (expired) {
    return (
      <Link
        to="/app/my-plan"
        className="inline-flex items-center gap-1.5 rounded-full bg-rose-600 px-3 py-1 text-[11px] font-semibold text-white shadow hover:bg-rose-700 animate-pulse"
        data-testid="topbar-expired-renew"
      >
        <AlertTriangle size={12} />Expired — Renew now
      </Link>
    );
  }
  if (expiringSoon) {
    return (
      <div className="flex items-center gap-2" data-testid="topbar-expiring">
        <Badge
          variant="outline"
          className="border-amber-300 bg-amber-50 text-amber-800 font-medium"
          title={`Plan: ${plan?.name || "—"} · Expires ${valid_until?.slice(0,10) || "—"}`}
        >
          <CalendarClock size={11} className="mr-1" />
          {days_left} day{days_left === 1 ? "" : "s"} left
        </Badge>
        <Link
          to="/app/my-plan"
          className="inline-flex items-center gap-1 rounded-md bg-brand px-3 py-1 text-[11px] font-semibold text-white shadow hover:bg-brand-700"
          data-testid="topbar-renew-btn"
        >
          <Sparkles size={11} />Renew now
        </Link>
      </div>
    );
  }
  // > 30 days: subtle green
  return (
    <Link
      to="/app/my-plan"
      className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[11px] font-medium text-emerald-700 hover:bg-emerald-100"
      data-testid="topbar-days-left"
      title={`Plan: ${plan?.name || "—"} · Valid until ${valid_until?.slice(0,10) || "—"}`}
    >
      <CalendarClock size={11} />{days_left} days left
    </Link>
  );
}
